import { Component, OnInit, OnDestroy } from '@angular/core';
import { Store, Select } from '@ngxs/store';
import {
  InitiateCall,
  UpdateCall,
  QueueCall,
  DismissCallAlert,
  LoadUnendedCall,
  ClearActiveCall,
  HoldCall,
  UnHoldCall,
  InitiateSecondCall,
  SwapCalls,
  MergeCalls,
  MuteState,
  TransferCall,
  CancelCall, 
  ToggleSilentMonitorMuteState
} from 'app/store/call/call.actions';
import {
  ActiveCall,
  Call,
  CallMuteStatus,
  CallTimers,
  CallUpdates,
} from 'app/models/dto/call.model';
import { CallState } from 'app/store/call/call.state';
import { Observable, Subscription, combineLatest, Subject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { WrapUpDialogComponent } from 'app/call-display/call-display/wrap-up-dialog/wrap-up-dialog.component';
import { GetUserQueue, DeleteBorrowerFromUserQueue } from 'app/store/queue/queue.actions';
import { QueueState } from 'app/store/queue/queue.state';
import { Queue } from 'app/models/queue/queue.model';
import { Borrower } from 'app/models/dto/borrower.model';
import { Alert } from 'app/models/dto/alert.model';
import { take, takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { LenderUserState } from 'ng2-app/store/lender-user/lender-user/lender-user.state';
import { LenderUser } from 'ng2-app/models/dto/lender-user.model';
import { CheckNewLeadCallTask } from 'ng2-app/modules/today/state/user-action-items.action';

@Component({
  selector: 'app-call-display-smart',
  templateUrl: './call-display-smart.component.html',
  styleUrls: ['./call-display-smart.component.scss']
})
export class CallDisplaySmartComponent implements OnInit, OnDestroy {

  @Select(CallState.call) call$: Observable<ActiveCall>;
  @Select(CallState.secondaryCall) secondaryCall$: Observable<ActiveCall>;
  @Select(CallState.dispositionCall) dispositionCall$: Observable<ActiveCall>;
  @Select(CallState.processing) callStateIsProcessing$: Observable<boolean>;
  @Select(CallState.twilioDeviceIsLoading) twilioDeviceIsLoading$: Observable<boolean>;
  @Select(CallState.silentMonitorInProgress) silentMonitorInProgress$: Observable<boolean>;
  @Select(CallState.alerts) alerts$: Observable<Alert[]>;
  @Select(CallState.callTimers) callTimers$: Observable<CallTimers>;
  @Select(CallState.callMuteStatus) callMuteStatus$: Observable<CallMuteStatus>;
  @Select(QueueState.queue) queue$: Observable<Queue[]>;
  @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;
  @Select(LenderUserState.hasPermission('canUnmuteSilentMonitorCall')) canUnmuteSilentMonitorCall$: Observable<boolean>;

  destroyed$ = new Subject<boolean>();
  activeCall: ActiveCall;
  secondaryCall: ActiveCall;
  dispositionCall: ActiveCall;
  callSub: Subscription;
  calledBorrower: Borrower;
  currentQueue: Queue[];
  queueSub: Subscription;

  wrapUpOpen = false;
  unendedCall = false;
  timerOpen: boolean;

  constructor(
    private store: Store,
    private dialog: MatDialog,
    private router: Router
  ) { }

  ngOnInit() {
    this.store.dispatch(new GetUserQueue());
    this.checkForUnendedCall();
    this.queueSub = this.queue$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(queue => this.currentQueue = queue);
    this.callSub = this.call$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(call => {
        this.activeCall = call;
        if (call && call.borrower) {
          this.activeCall = call;
          this.calledBorrower = call.borrower;
        }
      });
    this.dispositionCall$.pipe(takeUntil(this.destroyed$)).subscribe(disCall => this.dispositionCall = disCall);
    this.secondaryCall$.pipe(takeUntil(this.destroyed$)).subscribe(secondCall => this.secondaryCall = secondCall);
    this.callStateIsProcessing$.pipe(takeUntil(this.destroyed$)).subscribe();
    this.silentMonitorInProgress$.pipe(takeUntil(this.destroyed$)).subscribe();
    this.callTimers$.pipe(takeUntil(this.destroyed$)).subscribe();
    this.callMuteStatus$.pipe(takeUntil(this.destroyed$)).subscribe();

    // collapse the callbar once the queue has been cleared and there is no active call
    combineLatest([this.call$, this.queue$]).subscribe(
      ([call, queue]) => {
        if (call && !!call.callId !== true && !queue.length) {
          this.store.dispatch(new ClearActiveCall());
        }
      }
    );
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  checkForUnendedCall() {
    const unendedCall = JSON.parse(localStorage.getItem('currentCall') || '{}');
    if (unendedCall.callId && !/^(conference|internal|add) call/i.exec(<string> unendedCall.disposition)) {
      this.unendedCall = true;
      this.store.dispatch(new LoadUnendedCall(unendedCall.callId));
    }
  }

  initiateCall(call: Call): void {
    this.store.dispatch(new InitiateCall(call));
    this.router.navigate(['/borrower/', call.borrowerId]);
  }

  endCall(updates: CallUpdates): void {
    this.store.dispatch(new UpdateCall(updates));
  }

  updateCall(updates: CallUpdates): void {
    this.store.dispatch(new UpdateCall(updates));
  }

  destroyCall(callId): void {
    this.store.dispatch(new CancelCall);
    this.timerOpen = false;
    localStorage.removeItem('currentCall');
  }

  muteCall(setting): void {
    this.store.dispatch(new MuteState(setting));
  }

  toggleSilentMonitorMuteState(setting): void {
    this.store.dispatch(new ToggleSilentMonitorMuteState(setting));
  }

  placeCallOnHold(holdStatus: boolean): void {
    if (holdStatus && !!this.activeCall.callId) {
      this.store.dispatch(new HoldCall(this.activeCall.callId));
    } else if (!holdStatus && !!this.activeCall.callId) {
      this.store.dispatch(new UnHoldCall(this.activeCall.callId));
    }
  }

  transferCall(callInfo: object): void {
    this.store.dispatch(new TransferCall(callInfo));
  }

  startSecondCall(callInfo: object): void {
    this.store.dispatch(new InitiateSecondCall(callInfo));
  }

  swapCalls(): void {
    this.store.dispatch(new SwapCalls());
  }

  mergeCalls(): void {
    this.store.dispatch(new MergeCalls());
  }

  queueCall(call: Queue): void {
    this.store.dispatch(new QueueCall(call));
  }

  advanceQueue(): void {
    if (this.activeCall.borrowerId === this.currentQueue[0].borrowerId) {
      this.router.navigate(['/borrower/', this.currentQueue[0].borrowerId]);
    } else {
      this.queueCall(this.currentQueue[0]);
    }
  }

  shouldQueueAdvance(): boolean {
    return this.currentQueue &&
      this.activeCall &&
      this.currentQueue.length > 0 &&
      !this.activeCall.inProgress;
  }

  wrapUpDialog(checkEndCall: boolean) {
    if (this.wrapUpOpen) {
      return;
    }
    this.wrapUpOpen = true;

    const dialogRef = this.dialog.open(WrapUpDialogComponent, {
      disableClose: true,
      data: {
        activeCall: this.activeCall,
        checkEndCall,
        borrowersInQueue: this.borrowersInQueue(),
        unendedCall: this.unendedCall,
        unknownCaller: this.unknownCaller(),
      },
      panelClass: 'wrapUpDialogContainer',
      backdropClass: 'wrapUpBackdrop',
      position: { top: '150px' },
    });

    dialogRef.componentInstance.confirmedEndCall
      .pipe(takeUntil(this.destroyed$))
      .subscribe((updates: CallUpdates) => {
        this.endCall(updates);
        dialogRef.close();
        this.wrapUpOpen = false;
      }
    );

    dialogRef.componentInstance.updateEndedCall
      .pipe(takeUntil(this.destroyed$))
      .subscribe((updates: CallUpdates) => {
        this.updateCall(updates);
        dialogRef.close();
        this.completeCallWrapUp();
      }
    );

    dialogRef.afterClosed().subscribe(() => {
      if (!this.dispositionCall) {
        this.wrapUpOpen = false;
      }
    });
  }

  completeCallWrapUp() {
    //if we are on the today page, update today badge counts
    const borrowerId = this.activeCall?.borrowerId;
    if(this.router.url.includes('today')) {
      this.lenderUser$.pipe(take(1))
        .subscribe( lenderUser => {
          if(borrowerId) {
            this.store.dispatch( new CheckNewLeadCallTask(lenderUser.id, borrowerId) );
          }
        });
    }
    this.removeFromCallQueue();
    this.wrapUpOpen = false;
    this.unendedCall = false;
    if (this.shouldClearActiveCall()) {
      this.timerOpen = false;
      localStorage.removeItem('currentCall');
      this.store.dispatch(new ClearActiveCall());
      if (this.shouldQueueAdvance()) {
        this.advanceQueue();
      }
    }
  }

  borrowersInQueue() {
    return ((
      this.currentQueue.length > 0
      && this.currentQueue[0].borrowerId !== this.activeCall.borrowerId
    ) || (
      this.currentQueue.length > 1
      && this.currentQueue[0].borrowerId === this.activeCall.borrowerId
    ));
  }

  shouldClearActiveCall() {
    return (
      this.dispositionCall &&
      this.activeCall &&
      this.dispositionCall.callId === this.activeCall.callId
    ) || (
      this.activeCall && this.activeCall.callState === 'disconnected' &&
      !this.secondaryCall
    ) || (
      this.activeCall && /^(conference|internal|add) call/i.exec(<string> this.activeCall.disposition) &&
      !this.secondaryCall
    );
  }

  unknownCaller() {
    return (
      this.activeCall
      && (this.activeCall.borrowerId === -1 || !this.activeCall.borrowerId)
      && this.activeCall.businessName === 'Unknown Caller'
    );
  }

  removeFromCallQueue() {
    if (
      (!!this.currentQueue && this.currentQueue.length > 0)
      && this.currentQueue.filter(queuedItem => (queuedItem.borrowerId === this.activeCall.borrowerId) && this.activeCall.callId).length > 0
    ) {
      this.store.dispatch(new DeleteBorrowerFromUserQueue(this.activeCall.borrowerId));
    }
  }

  dismissCallAlert(alertIndex: number) {
    this.store.dispatch(new DismissCallAlert(alertIndex));
  }
}
